import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import BulkImageUploader from "@/components/BulkImageUploader";
import { toDownloadUrl, resolveMediaUrl } from "@/lib/download";
import { Camera, Video, Trash2, Plus, AlertCircle, Download, ImagePlus } from "lucide-react";

export default function MediaManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", media_url: "", priority: 0 });
  const [err, setErr] = useState("");
  const refresh = () => api.get("/media").then((r) => setItems(r.data));
  useEffect(() => { refresh(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addVideo = async (e) => {
    e.preventDefault();
    if (!form.media_url) { setErr("Please upload a video."); return; }
    setErr("");
    try {
      await api.post("/media", { ...form, category: "video", priority: Number(form.priority) || 0 });
      setForm({ title: "", media_url: "", priority: 0 });
      refresh();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
  };
  const remove = async (id) => { if (window.confirm("Remove this media item?")) { await api.delete(`/media/${id}`); refresh(); } };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="media-manager">
      <div className="flex items-center gap-2">
        <Camera className="h-5 w-5 text-nkf-red" />
        <h2 className="font-display text-xl text-nkf-blue">Media Center</h2>
      </div>
      <p className="text-sm text-slate-600 mt-1">
        Upload photos in bulk (auto-watermarked with the NKF logo) or add a video URL. Everything is downloadable on the public site.
      </p>

      {/* Bulk photo upload */}
      <div className="mt-5">
        <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold mb-2 flex items-center gap-1.5">
          <ImagePlus className="h-3.5 w-3.5" /> Bulk photo upload
        </div>
        <BulkImageUploader onDone={refresh} testid="media-bulk-upload" />
      </div>

      {/* Single video upload */}
      <form onSubmit={addVideo} className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 border-t border-slate-200 pt-5">
        <div className="md:col-span-12 text-[11px] uppercase tracking-widest text-nkf-red font-semibold flex items-center gap-1.5">
          <Video className="h-3.5 w-3.5" /> Add a video
        </div>
        <label className="md:col-span-7 block">
          <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Title (optional)</span>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. National Championship 2026 — Highlights" className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm" />
        </label>
        <label className="md:col-span-2 block">
          <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">Priority</span>
          <input type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm" />
        </label>
        <div className="md:col-span-9">
          <FileUpload value={form.media_url} onChange={(v) => set("media_url", v)} label="Video file *" accept="video/*" />
        </div>
        <button className="md:col-span-3 self-end inline-flex items-center justify-center gap-1 bg-nkf-blue hover:bg-nkf-navy text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded transition">
          <Plus className="h-3.5 w-3.5" /> Add Video
        </button>
      </form>
      {err && <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}

      {/* Grid */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((m) => (
          <div key={m.id} className="relative group border border-slate-200 rounded-md overflow-hidden" data-testid={`media-item-${m.id}`}>
            {m.category === "video" ? (
              <video src={resolveMediaUrl(m.media_url)} className="w-full aspect-square object-cover bg-slate-900" controls />
            ) : (
              <img src={resolveMediaUrl(m.media_url)} alt={m.title || ""} className="w-full aspect-square object-cover" loading="lazy" />
            )}
            <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 bg-nkf-navy/80 text-white text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded">
              {m.category === "video" ? <Video className="h-3 w-3" /> : <Camera className="h-3 w-3" />} {m.category}
            </div>
            <div className="absolute top-1.5 right-1.5 flex items-center gap-1.5">
              <a href={toDownloadUrl(m.media_url, `${(m.title || "nkf-media").replace(/\s+/g, "_")}.jpg`)} className="bg-white/95 hover:bg-white text-nkf-navy p-1.5 rounded transition opacity-0 group-hover:opacity-100" aria-label="Download" data-testid={`media-download-${m.id}`}>
                <Download className="h-3.5 w-3.5" />
              </a>
              <button onClick={() => remove(m.id)} className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded transition opacity-0 group-hover:opacity-100" aria-label="Remove" data-testid={`media-delete-${m.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {m.title && <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-nkf-navy/85 to-transparent text-white text-xs px-2 py-1.5 truncate">{m.title}</div>}
          </div>
        ))}
        {!items.length && <div className="col-span-full text-center text-slate-400 text-sm py-8 border border-dashed rounded-md">No media uploaded yet.</div>}
      </div>
    </section>
  );
}
